import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import {Download} from "lucide-react";
import {useEffect, useState} from "react";
import {downloadBlob} from "@/utils/downloadBlob.ts";
import MainDisplayContainer from "../components/Layout/MainDisplayContainer";
import {LoggedInHeader} from "../components/Navigation/LoggedInHeader";
import useGetTaxStatementPdf from "../hooks/useGetTaxStatementPdf.tsx";
import useGetTaxYears from "../hooks/useGetTaxYears";

interface TaxStatementYearsResponse {
    years: number[];
}

export default function TaxStatementPage() {
    const getTaxYears = useGetTaxYears();
    const getTaxStatementPdf = useGetTaxStatementPdf();
    const [years, setYears] = useState<number[]>([]);
    const [selectedYear, setSelectedYear] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchYears = async () => {
            try {
                const response = await getTaxYears();

                if (!response) {
                    throw new Error("Failed to fetch available years");
                }

                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(errorText || "Failed to fetch available years");
                }

                const data: TaxStatementYearsResponse = await response.json();
                setYears(data.years);

                // Auto-select most recent year if available
                if (data.years.length > 0) {
                    setSelectedYear(data.years[0].toString());
                }
            } catch (err) {
                console.error("Error fetching tax statement years:", err);
                let errorMessage = "Failed to load years";
                if (err instanceof Error) {
                    errorMessage = err.message;
                }
                setError(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        fetchYears();
    }, [getTaxYears]);

    const handleDownload = async () => {
        if (!selectedYear) {
            return;
        }

        setDownloading(true);
        setError(null);

        try {
            const blob = await getTaxStatementPdf(selectedYear);

            if (!blob) {
                throw new Error("Failed to generate PDF");
            }

            downloadBlob(blob, `TaxStatement_${selectedYear}.pdf`);
        } catch (err) {
            let errorMessage = "Failed to download PDF";
            if (err instanceof Error) {
                errorMessage = err.message;
            }
            setError(errorMessage);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Stack sx={{flexDirection: "column", height: "100vh"}}>
            <LoggedInHeader />
            <MainDisplayContainer>
                <Box
                    sx={{
                        height: "100%",
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        px: 2,
                        pt: 2,
                    }}
                >
                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100%",
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Card sx={{maxWidth: 800, mx: "auto", width: "100%"}}>
                            <CardContent sx={{p: 3}}>
                                <Typography
                                    variant="h4"
                                    component="h1"
                                    gutterBottom
                                    sx={{color: "primary.main", mb: 3}}
                                >
                                    IL Tax Statement
                                </Typography>

                                {error && (
                                    <Alert
                                        severity="error"
                                        onClose={() => setError(null)}
                                        sx={{mb: 3}}
                                    >
                                        {error}
                                    </Alert>
                                )}

                                {years.length === 0 ? (
                                    <Alert severity="info">
                                        No payment records found. Tax statements are only available
                                        for years in which you have made payments.
                                    </Alert>
                                ) : (
                                    <>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{mb: 3}}
                                        >
                                            Select a calendar year to download your IL Tax
                                            Statement. This statement lists all payments made during
                                            the selected year.
                                        </Typography>

                                        <FormControl fullWidth sx={{mb: 3}}>
                                            <InputLabel id="year-select-label">
                                                Select Calendar Year
                                            </InputLabel>
                                            <Select
                                                labelId="year-select-label"
                                                value={selectedYear}
                                                label="Select Calendar Year"
                                                onChange={e => setSelectedYear(e.target.value)}
                                                disabled={downloading}
                                            >
                                                {years.map(year => (
                                                    <MenuItem key={year} value={year}>
                                                        {year}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <Button
                                            variant="contained"
                                            size="large"
                                            fullWidth
                                            onClick={handleDownload}
                                            disabled={!selectedYear || downloading}
                                            startIcon={
                                                downloading ? (
                                                    <CircularProgress size={20} color="inherit" />
                                                ) : (
                                                    <Download size={20} />
                                                )
                                            }
                                        >
                                            {downloading
                                                ? "Generating PDF..."
                                                : "Download Tax Statement"}
                                        </Button>

                                        <Alert severity="info" sx={{mt: 3}}>
                                            <strong>Note:</strong> This statement is provided for
                                            informational purposes only. Please consult with a
                                            qualified tax professional to determine which fees may
                                            be eligible for tax deductions.
                                        </Alert>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </MainDisplayContainer>
        </Stack>
    );
}
